import {useCallback, useMemo} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {ActionCodesService} from '../api/generated/ActionCodesService';
import {BasicPage} from '../components/basic-page';
import {BreadcrumbPart} from '../components/page-title.tsx';
import {ROUTES} from '../routes';
import {useNotification} from '../hooks/use-notifications.tsx';
import {SubmitHandler} from 'react-hook-form';
import {actionCodeSchema, ActionCodeSchema} from './schemas.ts';
import {createForm} from '../forms';
import {Button, Group, Stack} from '@mantine/core';
import {useTypedParams} from 'react-router-typesafe-routes/dom';
import {useAsync} from 'react-use';
import {AsyncStateContainer} from '../components/async-state-container.tsx';
import {css} from '@emotion/react';
import {getDefaults} from '../utils/zod.ts';

const breadcrumbs: BreadcrumbPart[] = [
  {title: 'Action Codes', href: ROUTES.ADMIN.ACTION_CODES.INDEX.path},
  {title: 'Create'},
];

const Form = createForm<ActionCodeSchema>();

export const ActionCodeCreate = () => {
  const navigate = useNavigate();
  const notifications = useNotification();

  const onSubmit = useCallback<SubmitHandler<ActionCodeSchema>>(
    async (value) => {
      const response = await ActionCodesService.create({body: value});

      if (response.hasErrors) {
        return response;
      }

      notifications.success('Action Code Created');
      navigate(ROUTES.ADMIN.ACTION_CODES.INDEX.path);
    },
    [navigate, notifications],
  );

  return (
    <BasicPage title={breadcrumbs}>
      <Form
        defaultValues={getDefaults(actionCodeSchema)}
        schema={actionCodeSchema}
        onSubmit={onSubmit}
      >
        <Form.Container>
          <FormFields />
          <Group mt={24} justify={'flex-end'}>
            <Button
              type='button'
              variant='outline'
              component={Link}
              to={ROUTES.ADMIN.ACTION_CODES.INDEX.path}
            >
              Cancel
            </Button>
            <Form.Submit>Create Action Code</Form.Submit>
          </Group>
        </Form.Container>
      </Form>
    </BasicPage>
  );
};

export const ActionCodeUpdate = () => {
  const navigate = useNavigate();
  const notifications = useNotification();
  const {id} = useTypedParams(ROUTES.ADMIN.ACTION_CODES.DETAIL);

  const fetchActionCode = useAsync(async () => {
    const {data} = await ActionCodesService.getById({
      id,
    });
    return data;
  }, [id]);

  const actionCode = fetchActionCode.value || undefined;
  const actionCodeName = actionCode?.description ?? '';

  const breadcrumbs = useMemo<BreadcrumbPart[]>(
    () => [
      {title: 'Action Codes', href: ROUTES.ADMIN.ACTION_CODES.INDEX.path},
      {title: actionCodeName},
    ],
    [actionCodeName],
  );

  const onSubmit = useCallback<SubmitHandler<ActionCodeSchema>>(
    async (value) => {
      const response = await ActionCodesService.update({id, body: value});

      if (response.hasErrors) {
        return response;
      }

      notifications.success('Action Code Updated');
      navigate(ROUTES.ADMIN.ACTION_CODES.INDEX.path);
    },
    [id, navigate, notifications],
  );

  return (
    <BasicPage title={breadcrumbs}>
      <AsyncStateContainer {...fetchActionCode}>
        <Form
          schema={actionCodeSchema}
          onSubmit={onSubmit}
          defaultValues={actionCode}
        >
          <Form.Container>
            <FormFields />
            <Group mt={24} justify='flex-end'>
              <Button
                type='button'
                variant='outline'
                component={Link}
                to={ROUTES.ADMIN.ACTION_CODES.INDEX.path}
              >
                Cancel
              </Button>
              <Form.Submit>Update Action Code</Form.Submit>
            </Group>
          </Form.Container>
        </Form>
      </AsyncStateContainer>
    </BasicPage>
  );
};

const FormFields = () => {
  return (
    <>
      <Form.Row css={unlimitedChildWidth}>
        <Form.TextInput
          name='description'
          label='Description'
          placeholder='Description'
        />
        <Stack w='10%' css={withoutFlexGrow}>
          <Form.NumberInput name='sortOrder' label='Sort Order' />
        </Stack>
        <Stack w='10%' css={withoutFlexGrow}>
          <Form.ColorInput name='color' label='Color' />
        </Stack>
      </Form.Row>
      <Form.Row mt={12} ml={12} w='66%'>
        <Form.Checkbox name='inactive' label='Inactive' />
        <Form.Checkbox name='isDefault' label='Is Default' />
        <Form.Checkbox name='requireLot' label='Require Lot' />
        <Form.Checkbox name='requireDisposition' label='Require Disposition' />
      </Form.Row>
    </>
  );
};

const withoutFlexGrow = css({
  flexGrow: 0,
});

const unlimitedChildWidth = css({
  '& > *': {
    maxWidth: '100%',
  },
});
